import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, Box, Network } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { useBodies, useJoints, useSites, useTendons } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePanelStore } from "../../stores/panelStore";
import { useNavigateToObjectFromBodyUuid } from "../../hooks/useNavigateToObjectFromBodyUuid";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { buildBodyParentMap, findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { EntireObjectIcon } from "../ui/Icons";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

const NAV_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded text-gray-100 transition-colors " +
  "hover:bg-gray-700/60 hover:text-white " +
  "disabled:cursor-not-allowed disabled:text-gray-200 disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-200";

const BODY_NAV_PANELS = new Set(["body", "inertial", "geom", "visualGeom", "joint", "site"]);

const resolveJointUuid = (joints, jointRef) => {
  if (!jointRef) return null;
  const ref = String(jointRef);
  if (joints?.[ref]?.uuid) return joints[ref].uuid;
  return Object.values(joints || {}).find((joint) => joint?.uuid === ref || joint?.name === ref)
    ?.uuid;
};

const findBodyForSiteRef = (bodies, sites, siteRef) => {
  if (!siteRef) return null;
  const ref = String(siteRef);
  const siteUuid =
    sites?.[ref]?.uuid ||
    Object.values(sites || {}).find((site) => site?.uuid === ref || site?.name === ref)?.uuid ||
    ref;
  for (const body of Object.values(bodies || {})) {
    if (Array.isArray(body?.sites) && body.sites.some((uuid) => String(uuid) === siteUuid)) {
      return body.uuid;
    }
  }
  return null;
};

const findBodyForGeomRef = (bodies, geomRef) => {
  if (!geomRef) return null;
  const ref = String(geomRef);
  for (const body of Object.values(bodies || {})) {
    if (
      Array.isArray(body?.geoms) &&
      body.geoms.some((geom) => String(geom?.uuid) === ref || String(geom?.name) === ref)
    ) {
      return body.uuid;
    }
  }
  return null;
};

const buildTendonBodyTargets = ({ tendon, bodies, joints, sites }) => {
  const seen = new Set();
  const targets = [];
  const addBody = (bodyUuid) => {
    if (!bodyUuid || seen.has(bodyUuid)) return;
    seen.add(bodyUuid);
    targets.push({
      uuid: bodyUuid,
      name: bodies?.[bodyUuid]?.name || bodyUuid,
    });
  };

  if (Array.isArray(tendon?.joints)) {
    tendon.joints.forEach((entry) => {
      const jointUuid = resolveJointUuid(joints, entry?.joint);
      addBody(jointUuid ? findBodyForJointUuid(bodies || {}, jointUuid) : null);
    });
  }

  if (Array.isArray(tendon?.spatial)) {
    tendon.spatial.forEach((entry) => {
      addBody(findBodyForSiteRef(bodies, sites, entry?.site));
      addBody(findBodyForSiteRef(bodies, sites, entry?.sidesite));
      addBody(findBodyForGeomRef(bodies, entry?.geom));
    });
  }

  return targets;
};

const NumberedBodyIcon = ({ slot }) => (
  <span className="relative block h-4 w-4" aria-hidden>
    <Box className="absolute inset-0 h-4 w-4" />
    <span
      className="pointer-events-none absolute right-[-5px] bottom-[-1px] text-[8px] font-bold leading-none text-gray-100"
      style={{ textShadow: "0 0 2px rgb(0 0 0), 0 0 1px rgb(0 0 0)" }}
    >
      {slot}
    </span>
  </span>
);

const PinnedInspectorNav = ({ backTarget, onBack, forwardTarget, onForward, activePanel }) => {
  const [childMenuOpen, setChildMenuOpen] = useState(false);
  const childMenuRef = useRef(null);
  const setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);

  const selectedBody = useSelectionStore((s) => s.selectedBody);
  const selectedGeom = useSelectionStore((s) => s.selectedGeom);
  const selectedJoint = useSelectionStore((s) => s.selectedJoint);
  const selectedSite = useSelectionStore((s) => s.selectedSite);
  const selectedInertial = useSelectionStore((s) => s.selectedInertial);
  const selectedTendon = useSelectionStore((s) => s.selectedTendon);

  const bodies = useBodies();
  const joints = useJoints();
  const sites = useSites();
  const tendons = useTendons();

  const parentBodyUuid = useMemo(() => {
    switch (activePanel) {
      case "body":
        for (const body of Object.values(bodies || {})) {
          if (Array.isArray(body.children) && body.children.includes(selectedBody))
            return body.uuid;
        }
        return null;
      case "inertial": {
        const str = String(selectedInertial || "");
        const sep = str.indexOf(":");
        return sep > 0 ? str.slice(0, sep) : null;
      }
      case "geom":
      case "visualGeom": {
        const str = String(selectedGeom || "");
        const sep = str.indexOf(":");
        return sep > 0 ? str.slice(0, sep) : null;
      }
      case "joint":
        for (const body of Object.values(bodies || {})) {
          if (Array.isArray(body.joints) && body.joints.includes(selectedJoint)) return body.uuid;
        }
        return null;
      case "site":
        for (const body of Object.values(bodies || {})) {
          if (Array.isArray(body.sites) && body.sites.includes(selectedSite)) return body.uuid;
        }
        return null;
      default:
        return null;
    }
  }, [
    activePanel,
    selectedBody,
    selectedGeom,
    selectedJoint,
    selectedSite,
    selectedInertial,
    bodies,
  ]);

  const childBodies = useMemo(() => {
    if (activePanel !== "body" || !selectedBody) return [];
    const body = bodies?.[selectedBody];
    return (Array.isArray(body?.children) ? body.children : []).map((uuid) => ({
      uuid,
      name: bodies?.[uuid]?.name || uuid,
    }));
  }, [activePanel, selectedBody, bodies]);

  const tendonBodyTargets = useMemo(() => {
    if (activePanel !== "tendon" || !selectedTendon) return [];
    return buildTendonBodyTargets({
      tendon: tendons?.[selectedTendon],
      bodies,
      joints,
      sites,
    });
  }, [activePanel, bodies, joints, selectedTendon, sites, tendons]);

  const hierarchyRootUuid = useMemo(() => {
    if (activePanel !== "object" || !selectedBody) return null;
    const parentMap = buildBodyParentMap(bodies || {});
    let current = selectedBody;
    const visited = new Set();
    while (current && !visited.has(current)) {
      visited.add(current);
      const parent = parentMap[current];
      if (!parent) break;
      current = parent;
    }
    return current || null;
  }, [activePanel, selectedBody, bodies]);

  const navSourceBodyUuid = activePanel === "body" ? selectedBody : parentBodyUuid;
  const { objectRootUuid, onNavigateToObject } = useNavigateToObjectFromBodyUuid(navSourceBodyUuid);

  const navigateToBody = useCallback((bodyUuid) => {
    if (!bodyUuid) return;
    const selection = useSelectionStore.getState();
    requestInspectorChromeFlash();
    selection.setLastSelectionSource("sceneTree");
    if (selection.selectedBody !== bodyUuid) selection.selectBody(bodyUuid);
  }, []);

  useEffect(() => {
    if (!childMenuOpen) return undefined;
    const handle = (e) => {
      if (!childMenuRef.current?.contains(e.target)) setChildMenuOpen(false);
    };
    window.addEventListener("pointerdown", handle, true);
    return () => window.removeEventListener("pointerdown", handle, true);
  }, [childMenuOpen]);

  useEffect(() => {
    const isEditable = (target) => {
      if (!target || typeof target.closest !== "function") return false;
      return Boolean(
        target.closest("input, textarea, select, [contenteditable], [role='textbox']")
      );
    };

    const handleKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isEditable(e.target)) return;

      if (e.key === "ArrowLeft" && backTarget) {
        e.preventDefault();
        e.stopPropagation();
        onBack(backTarget);
        return;
      }
      if (e.key === "ArrowRight" && forwardTarget) {
        e.preventDefault();
        e.stopPropagation();
        onForward(forwardTarget);
        return;
      }
      if (e.key === "ArrowUp" && parentBodyUuid) {
        e.preventDefault();
        e.stopPropagation();
        navigateToBody(parentBodyUuid);
        return;
      }
      if (e.key === "ArrowDown" && childBodies.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        navigateToBody(childBodies[0].uuid);
        return;
      }
      if (e.key === "ArrowDown" && childBodies.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        setChildMenuOpen((open) => !open);
      }
    };

    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [backTarget, forwardTarget, parentBodyUuid, childBodies, onBack, onForward, navigateToBody]);

  const canGoBack = Boolean(backTarget);
  const canGoForward = Boolean(forwardTarget);
  const showBodyNav = BODY_NAV_PANELS.has(activePanel);
  const showTendonBodyNav = activePanel === "tendon" && tendonBodyTargets.length > 0;
  const hasParentBody = Boolean(parentBodyUuid);
  const hasChildBodies = childBodies.length > 0;
  const canNavigateToObject = Boolean(objectRootUuid) && activePanel !== "object";
  const showObjectRootBody = activePanel === "object";
  const canInspectRootBody = Boolean(hierarchyRootUuid);

  const backLabel = canGoBack ? `Back: ${backTarget.label}` : "Back (ALT+←)";
  const forwardLabel = canGoForward ? `Forward: ${forwardTarget.label}` : "Forward (ALT+→)";
  const parentLabel = parentBodyUuid
    ? `Parent body (ALT+↑): ${stripPrefix(bodies?.[parentBodyUuid]?.name) || parentBodyUuid}`
    : "Parent body (ALT+↑)";
  const childLabel = (() => {
    if (!hasChildBodies) return "Child body (ALT+↓)";
    if (childBodies.length === 1)
      return `Child body (ALT+↓): ${stripPrefix(childBodies[0].name) || childBodies[0].uuid}`;
    return `Child bodies (ALT+↓)`;
  })();

  const rootBodyLabel = hierarchyRootUuid
    ? `Inspect Root Body: ${stripPrefix(bodies?.[hierarchyRootUuid]?.name) || hierarchyRootUuid}`
    : "Inspect Root Body";

  return (
    <div className="shrink-0 flex w-full justify-center pt-1.5">
      <div className="relative flex w-full items-center justify-center gap-0.5 rounded-md border border-gray-600/60 bg-[rgba(42,42,46,0.97)] px-1.5 py-1 shadow-lg shadow-black/35">
        <Tooltip content={backLabel}>
          <button
            type="button"
            disabled={!canGoBack}
            aria-label={backLabel}
            onClick={() => {
              if (canGoBack) onBack(backTarget);
            }}
            className={NAV_BTN}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden />
          </button>
        </Tooltip>

        <Tooltip content={forwardLabel}>
          <button
            type="button"
            disabled={!canGoForward}
            aria-label={forwardLabel}
            onClick={() => {
              if (canGoForward) onForward(forwardTarget);
            }}
            className={NAV_BTN}
          >
            <ArrowRight className="w-4 h-4" aria-hidden />
          </button>
        </Tooltip>

        {showBodyNav && (
          <>
            <div className="mx-1 h-4 w-px bg-gray-600/60 shrink-0" aria-hidden />

            <Tooltip content={parentLabel}>
              <button
                type="button"
                disabled={!hasParentBody}
                aria-label={parentLabel}
                onClick={() => navigateToBody(parentBodyUuid)}
                className={NAV_BTN}
              >
                <ArrowUp className="w-4 h-4" aria-hidden />
              </button>
            </Tooltip>

            {activePanel === "body" && (
              <div ref={childMenuRef} className="relative">
                <Tooltip content={childLabel}>
                  <button
                    type="button"
                    disabled={!hasChildBodies}
                    aria-label={childLabel}
                    onClick={() => {
                      if (!hasChildBodies) return;
                      if (childBodies.length === 1) {
                        navigateToBody(childBodies[0].uuid);
                        return;
                      }
                      setChildMenuOpen((o) => !o);
                    }}
                    className={NAV_BTN}
                  >
                    <ArrowDown className="w-4 h-4" aria-hidden />
                  </button>
                </Tooltip>
                {childMenuOpen && childBodies.length > 1 && (
                  <div
                    className="absolute bottom-full left-0 mb-1 min-w-[160px] rounded-md border border-gray-600 p-0.5 shadow-xl z-[10061]"
                    style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                  >
                    {childBodies.map((child) => (
                      <button
                        key={child.uuid}
                        type="button"
                        onClick={() => {
                          navigateToBody(child.uuid);
                          setChildMenuOpen(false);
                        }}
                        className="w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                      >
                        {stripPrefix(child.name) || child.uuid}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {canNavigateToObject && (
              <Tooltip content="Inspect parent object">
                <button
                  type="button"
                  aria-label="Inspect parent object"
                  onClick={onNavigateToObject}
                  className={NAV_BTN}
                >
                  <EntireObjectIcon className="w-4 h-4" aria-hidden />
                </button>
              </Tooltip>
            )}
          </>
        )}

        {showTendonBodyNav && (
          <>
            <div className="mx-1 h-4 w-px bg-gray-600/60 shrink-0" aria-hidden />

            {tendonBodyTargets.map((target, index) => {
              const label = `Tendon body ${index + 1}: ${stripPrefix(target.name) || target.uuid}`;
              return (
                <Tooltip key={target.uuid} content={label}>
                  <button
                    type="button"
                    aria-label={label}
                    onClick={() => navigateToBody(target.uuid)}
                    className={NAV_BTN}
                  >
                    <NumberedBodyIcon slot={index + 1} />
                  </button>
                </Tooltip>
              );
            })}
          </>
        )}

        {showObjectRootBody && (
          <>
            <div className="mx-1 h-4 w-px bg-gray-600/60 shrink-0" aria-hidden />

            <Tooltip content={rootBodyLabel}>
              <button
                type="button"
                disabled={!canInspectRootBody}
                aria-label={rootBodyLabel}
                onClick={() => navigateToBody(hierarchyRootUuid)}
                className={NAV_BTN}
              >
                <span className="relative block h-4 w-4" aria-hidden>
                  <Box className="absolute inset-0 h-4 w-4" />
                  <ArrowDown className="absolute -bottom-1 -right-1 h-2.5 w-2.5" />
                </span>
              </button>
            </Tooltip>
          </>
        )}

        <div className="mx-1 h-4 w-px bg-gray-600/50 shrink-0" aria-hidden />

        <Tooltip content="Open scene graph">
          <button
            type="button"
            aria-label="Open scene graph"
            onClick={() => setSceneGraphOpen(true)}
            className={NAV_BTN}
          >
            <Network size={16} aria-hidden />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default React.memo(PinnedInspectorNav);
